import React, { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { useNavigate } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Plus, RefreshCw, Search, Loader2 } from "lucide-react";
import { formatINR, cn } from "@/lib/utils";
import { QuoteStatusBadge } from "./QuoteStatusBadge";
import type { QuotationRecord } from "@/types";
import { FilterBar } from "@/components/filters/FilterBar";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import {
  CalendarDays as FilterDateIcon,
  ShieldCheck as FilterValidityIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const QUOTE_FILTER_DEFAULTS = { owner: "all", date: "any", validity: "all" };
const VALIDITY_OPTIONS = [
  { value: "all", label: "Any validity" },
  { value: "valid", label: "Still valid" },
  { value: "expiring", label: "Expiring in 7 days" },
  { value: "expired", label: "Expired" },
];

type Tab =
  | "ALL"
  | "MINE_TO_APPROVE"
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "SENT"
  | "ACCEPTED"
  | "REJECTED"
  | "EXPIRED";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "ALL", label: "All" },
  { id: "MINE_TO_APPROVE", label: "Needs my approval" },
  { id: "DRAFT", label: "Draft" },
  { id: "PENDING_APPROVAL", label: "Pending approval" },
  { id: "APPROVED", label: "Approved" },
  { id: "SENT", label: "Sent" },
  { id: "ACCEPTED", label: "Accepted" },
  { id: "REJECTED", label: "Rejected" },
  { id: "EXPIRED", label: "Expired" },
];

const formatDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

export function QuotesPage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission, user } = useAuth();
  const quoteFilters = usePersistentFilters("quotes", QUOTE_FILTER_DEFAULTS);
  const [quoteOptions, setQuoteOptions] = useState<{ total: number; owners: OwnerFacet[] }>({ total: 0, owners: [] });
  const canApprove = hasPermission("quote.approve");
  const canCreate = hasPermission("quote.create");

  const [tab, setTab] = useState<Tab>("ALL");
  const [search, setSearch] = useUrlSearch();
  const [approvalCount, setApprovalCount] = useState(0);

  // New quote dialog
  const [newOpen, setNewOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState("");
  const [selectedDealLabel, setSelectedDealLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const list = usePagedList<QuotationRecord>({
    deps: [tab, search, quoteFilters.values],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = quoteFilters.values;
      const query = toQuery({
        mine_to_approve: tab === "MINE_TO_APPROVE" ? "true" : undefined,
        status: tab !== "ALL" && tab !== "MINE_TO_APPROVE" ? tab : undefined,
        search: search.trim(),
        owner: f.owner,
        validity: f.validity,
        ...dateRangeParams(f.date, "date"),
        page,
        page_size: 30,
      });
      try {
        const [res, optRes] = await Promise.all([
          api.get(`/api/Quotation/List${query}`),
          api.get("/api/Quotation/FilterOptions").catch(() => null),
        ]);
        if (optRes) setQuoteOptions(unwrapObject(optRes));
        return unwrapPage<QuotationRecord>(res);
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load quotes");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const quotes = list.rows;
  const loading = list.loading;
  const loadQuotes = list.reload;

  useEffect(() => {
    if (!canApprove) return;
    api
      .get("/api/Quotation/List?mine_to_approve=true&page_size=1")
      .then((res) => setApprovalCount(unwrapPage(res).total))
      .catch(() => setApprovalCount(0));
  }, [canApprove, list.total]);

  const openNewQuote = () => {
    setSelectedDeal("");
    setSelectedDealLabel("");
    setNewOpen(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeal) {
      error("Select a Deal", "Choose the deal this quote is for");
      return;
    }
    setCreating(true);
    try {
      const res: any = await api.post("/api/Quotation/Insert", { deal_id: selectedDeal });
      success("Quote Created", "Draft quote created from the deal's products");
      setNewOpen(false);
      navigate(`/quotes/${res?.AddtionalData}`);
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create quote");
    } finally {
      setCreating(false);
    }
  };

  const visibleTabs = TABS.filter((t) => t.id !== "MINE_TO_APPROVE" || canApprove);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Quotations</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Create GST quotations from deals, get discounts approved and send them to customers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadQuotes} disabled={loading} className="h-9 gap-1.5">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canCreate && (
            <Button size="sm" onClick={openNewQuote} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" />
              New Quote
            </Button>
          )}
        </div>
      </div>

      {/* Tabs + search */}
      <div className="space-y-3">
        <div className="flex items-center gap-1 overflow-x-auto bg-card border rounded-xl p-1 text-xs font-semibold">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors flex items-center gap-1.5",
                tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {t.label}
              {t.id === "MINE_TO_APPROVE" && approvalCount > 0 && (
                <span
                  className={cn(
                    "text-[10px] px-1.5 rounded-full",
                    tab === t.id ? "bg-primary-foreground/20" : "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                  )}
                >
                  {approvalCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: "Search by quote number, customer or deal..." }}
          fields={[
            { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(quoteOptions.owners, quoteOptions.total, user?.Id), defaultValue: "all" },
            { name: "date", label: "Quote date", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
            { name: "validity", label: "Validity", icon: FilterValidityIcon, type: "select", options: VALIDITY_OPTIONS, defaultValue: "all" },
          ]}
          values={quoteFilters.values}
          onChange={(name, value) => quoteFilters.set(name as keyof typeof QUOTE_FILTER_DEFAULTS, value)}
          onReset={quoteFilters.reset}
          shown={list.total}
          total={list.total}
          noun="quotes"
        />
      </div>

      {/* Table */}
      {loading && quotes.length === 0 ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quote</TableHead>
                <TableHead>Customer / Deal</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead className="text-right">Grand Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow
                  key={q.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/quotes/${q.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                        <FileText className="size-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm font-mono">{q.display_number}</div>
                        {Number(q.max_discount_pct) > 0 && (
                          <div className="text-[11px] text-muted-foreground">
                            Max discount {Number(q.max_discount_pct)}%
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{q.account?.name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{q.deal?.name}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {q.owner ? `${q.owner.first_name} ${q.owner.last_name || ""}` : "-"}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(q.quote_date)}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(q.valid_until)}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">
                    {formatINR(q.grand_total)}
                  </TableCell>
                  <TableCell>
                    <QuoteStatusBadge status={q.quote_status} expired={q.is_expired} />
                  </TableCell>
                </TableRow>
              ))}
              {quotes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">
                    {tab === "MINE_TO_APPROVE" ? "Nothing waiting for your approval." : "No quotes found."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* New quote dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">New Quote</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Pick an open deal. Its products are copied into a draft quote you can edit.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5 py-2">
            <div className="space-y-2">
              <Label>Deal *</Label>
              <SearchSelect
                value={selectedDeal}
                selectedLabel={selectedDealLabel}
                endpoint="/api/Deal/List"
                extraParams={{ status: "OPEN" }}
                placeholder="Search open deals"
                getLabel={(d) => `${d.name}${d.account?.name ? ` · ${d.account.name}` : ""}`}
                onChange={(id, row) => {
                  setSelectedDeal(id);
                  setSelectedDealLabel(row ? `${row.name}${row.account?.name ? ` · ${row.account.name}` : ""}` : "");
                }}
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating || !selectedDeal}>
                {creating ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Create Draft
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
